/**
 * `P0-PLT-038`: what the flag diagnostic answers, and the narrowing that makes
 * it safe to render.
 *
 * **Parsed rather than cast, like `identityFrom` beside it.** The answer
 * crosses a network boundary, and a screen that trusted its shape would render
 * `undefined` into a table the first time the route changed. Every field is
 * checked; anything unrecognised makes the whole read `null`, which the card
 * reports as a refusal rather than as an empty environment.
 *
 * **`marker` is `null` for a real state and not for a missing one.** An empty
 * `platform.environment_marker` is exactly the condition that derives every flag
 * to false, so `null` here is the diagnosis, and the card prints it as a
 * sentence rather than as a blank cell.
 */

export interface DrapeauLu {
  readonly key: string;
  readonly label: string;
  readonly valueBoolean: boolean | null;
  readonly allowedEnvironments: readonly string[];
  readonly enabled: boolean;
}

export interface DiagnosticLu {
  readonly marker: string | null;
  readonly data: readonly DrapeauLu[];
}

function drapeauFrom(value: unknown): DrapeauLu | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  const key = row["key"];
  const label = row["label"];
  const allowed = row["allowedEnvironments"];
  if (typeof key !== "string" || typeof label !== "string" || !Array.isArray(allowed)) return null;

  const raw = row["valueBoolean"];
  return {
    key,
    label,
    // Three states and all three are real: true, false, and a boolean flag whose
    // column is NULL. The card prints the third as `faux`, which is what the
    // server's own `IS TRUE` does with it.
    valueBoolean: typeof raw === "boolean" ? raw : null,
    allowedEnvironments: allowed.filter((name): name is string => typeof name === "string"),
    enabled: row["enabled"] === true,
  };
}

export function diagnosticFrom(answer: unknown): DiagnosticLu | null {
  if (typeof answer !== "object" || answer === null) return null;
  const body = answer as Record<string, unknown>;
  const data = body["data"];
  if (!Array.isArray(data)) return null;

  const rows: DrapeauLu[] = [];
  for (const entry of data) {
    const row = drapeauFrom(entry);
    if (row === null) return null;
    rows.push(row);
  }

  const marker = body["marker"];
  return { marker: typeof marker === "string" ? marker : null, data: rows };
}
